<template>
  <Container class="flex flex-col items-center">
    <div class="w-full max-w-[650px]">
      <div
        v-if="data?.copy"
        class="custom-list prose lg:prose-lg prose-a:text-blue prose-a:no-underline font-sans text-base leading-[21px] lg:text-lg lg:leading-[26px]"
        :class="[
          'center',
          'mx-auto',
          blockquoteStyles,
          useDarkMode
            ? 'prose-p:text-white prose-ul:text-white prose-headings:text-white hover:prose-a:text-blue-hover'
            : 'hover:prose-a:text-blue-dark text-black',
        ]"
        v-html="data?.copy"
      ></div>

      <component
        :is="linkComponent"
        v-if="data?.link?.url && linkComponent"
        :title="data?.link?.title"
        :link="data.link.url"
        :external="data?.link?.type === 'ext'"
        :type="linkTypeForComponent"
        :star="data?.linkType === 'gold'"
        v-bind="linkProps"
        class="mt-6 lg:mt-8"
      />
    </div>
  </Container>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/DynamicBlocks/ContentTextBlock.astro
import { computed, inject, ref, type Ref } from 'vue'
import Link from '../atoms/Link.vue'
import LinkSecondary from '../atoms/LinkSecondary.vue'
import Container from '../atoms/Container.vue'
import type { ILinkData } from '../../types'

const blockquoteStyles = [
  'prose-blockquote:bg-blue-hover',
  'prose-blockquote:max-w-[650px]',
  'prose-blockquote:not-italic',
  'prose-blockquote:p-4',
  'prose-blockquote:md:p-8',
  'prose-blockquote:border-none',
  'prose-blockquote:relative',
  'prose-blockquote:mx-auto',
  'prose-blockquote:w-fit',
  'prose-blockquote:rounded-2xl',
  'prose-blockquote:rounded-tl-none',
].join(' ')

export interface IContentTextBlock {
  id: string
  copy?: string
  link?: ILinkData
  linkType?: string
}

interface Props {
  data: IContentTextBlock
}

const props = defineProps<Props>()

// Which component to render
const linkComponent = computed(() => {
  return props.data.linkType === 'blue-arrow' ? LinkSecondary : Link
})

// type prop for LinkSecondary
const linkTypeForComponent = computed(() => {
  return props.data?.linkType === 'blue-arrow' ? 'blue' : undefined
})

// For Link (extra props)
const linkProps = computed(() => {
  switch (props.data?.linkType) {
    case undefined:
    case 'blue-arrow':
      return {}
    case 'blue':
      return { type: 'fill', color: 'blue' }
    case 'blue-outline':
      return { type: 'outline', color: 'blue' }
    case 'pink':
      return { type: 'fill', color: 'pink' }
    case 'pink-outline':
      return { type: 'outline', color: 'pink' }
    case 'gold':
      return { type: 'fill', color: 'gold' }
    case 'gold-outline':
      return { type: 'outline', color: 'gold' }
    default:
      return { type: 'fill', color: 'blue' }
  }
})

const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme'))
</script>

<style scoped>
:deep(.prose blockquote p) {
  margin-bottom: 0;
}
:deep(.prose blockquote p:first-of-type::before),
:deep(.prose blockquote p:last-of-type::after) {
  content: none !important;
}
</style>
